/**
 * FailedDetectionNotification Component
 *
 * Toast notification displayed when a user-drawn detection fails
 * threshold validation and is rejected.
 *
 * For admins, shows detailed classification info including:
 * - Top classes with distances/scores
 * - Voting results
 * - Refinement scores
 */

import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

interface ClassificationDetails {
  top_classes?: Array<{ label: string; score: number; distance?: number; class_name?: string }>;
  predicted_class?: string;
  predicted_confidence?: number;
  voting_results?: {
    majority_class?: string;
    vote_counts?: Record<string, number>;
    total_votes?: number;
  };
  refinement_score?: number;
  distance_threshold?: number;
  avg_distance?: number;
}

interface FailedDetectionNotificationProps {
  notification: {
    message: string;
    visible: boolean;
    classificationDetails?: ClassificationDetails;
  } | null;
  isAdmin?: boolean;
}

export const FailedDetectionNotification: React.FC<FailedDetectionNotificationProps> = ({
  notification,
  isAdmin = false,
}) => {
  const [expanded, setExpanded] = useState(false);

  if (!notification) return null;

  const details = notification.classificationDetails;
  const hasDetails = isAdmin && details && (details.top_classes?.length || details.voting_results);

  return (
    <div
      className={`fixed top-4 right-4 bg-red-500 text-white px-4 py-3 rounded-lg shadow-lg z-50 transition-all duration-300 ${
        notification.visible ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-2"
      } ${hasDetails ? "max-w-lg" : "max-w-sm"}`}
    >
      <div className="flex items-start space-x-3">
        <div className="flex-shrink-0 mt-0.5">
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
              clipRule="evenodd"
            />
          </svg>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium">{notification.message}</p>

          {/* Admin details toggle */}
          {hasDetails && (
            <button
              onClick={() => setExpanded(!expanded)}
              className="mt-2 text-xs flex items-center gap-1 text-red-100 hover:text-white transition-colors"
            >
              {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              {expanded ? "Hide" : "Show"} classification details
            </button>
          )}

          {/* Expanded details for admin */}
          {hasDetails && expanded && (
            <div className="mt-3 pt-3 border-t border-red-400 text-xs space-y-2">
              {/* Threshold info */}
              {details.distance_threshold !== undefined && (
                <div className="flex justify-between">
                  <span className="text-red-200">Distance threshold:</span>
                  <span className="font-mono">{details.distance_threshold.toFixed(4)}</span>
                </div>
              )}
              {details.avg_distance !== undefined && (
                <div className="flex justify-between">
                  <span className="text-red-200">Avg distance:</span>
                  <span className="font-mono">{details.avg_distance.toFixed(4)}</span>
                </div>
              )}
              {details.refinement_score !== undefined && (
                <div className="flex justify-between">
                  <span className="text-red-200">Refinement score:</span>
                  <span className="font-mono">{details.refinement_score.toFixed(4)}</span>
                </div>
              )}

              {/* Voting results */}
              {details.voting_results && (
                <div className="mt-2">
                  <div className="text-red-200 mb-1">Voting results:</div>
                  <div className="pl-2 space-y-0.5">
                    {details.voting_results.majority_class && (
                      <div>Majority: <span className="font-medium">{details.voting_results.majority_class}</span></div>
                    )}
                    {details.voting_results.vote_counts && (
                      <div className="text-red-200">
                        Votes: {Object.entries(details.voting_results.vote_counts)
                          .sort(([,a], [,b]) => b - a)
                          .slice(0, 5)
                          .map(([cls, count]) => `${cls}: ${count}`)
                          .join(", ")}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Top classes */}
              {details.top_classes && details.top_classes.length > 0 && (
                <div className="mt-2">
                  <div className="text-red-200 mb-1">Top {Math.min(5, details.top_classes.length)} classes:</div>
                  <div className="space-y-1 max-h-32 overflow-y-auto">
                    {details.top_classes.slice(0, 5).map((cls, idx) => (
                      <div key={idx} className="flex justify-between items-center pl-2">
                        <span className="truncate flex-1 mr-2">
                          {idx + 1}. {cls.label || cls.class_name}
                        </span>
                        <span className="font-mono text-red-200 flex-shrink-0">
                          {cls.distance !== undefined
                            ? `d=${cls.distance.toFixed(3)}`
                            : cls.score !== undefined
                              ? `s=${cls.score.toFixed(3)}`
                              : ""}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FailedDetectionNotification;
